import type { ContextTermValue, JsonLdDocument } from '@dpem/ontology';
import * as z from 'zod';

/**
 * Transport shape of a JSON-LD method document.
 *
 * Why not `MethodGraphSchema` from the ontology: import must *accept* a
 * syntactically well-formed document and answer with ontology diagnostics, so
 * rejecting at the DTO boundary would destroy the feature. Export emits the
 * ontology's own serialisation, which is a superset-safe fit for this shape.
 * Node contents stay opaque here — the ontology owns node semantics.
 */
export const ContextTermValueSchema = z.union([
  z.string(),
  z.strictObject({ '@id': z.string(), '@type': z.literal('@id').optional() }),
]);

export const JsonLdNodeSchema = z.record(z.string(), z.unknown());

export const JsonLdDocumentSchema = z.strictObject({
  '@context': z.record(z.string(), ContextTermValueSchema),
  '@graph': z.array(JsonLdNodeSchema),
});
export type JsonLdDocumentDto = z.infer<typeof JsonLdDocumentSchema>;

/** Compile-time proof the transport shape still satisfies the ontology's document type. */
type Extends<TSub extends TSuper, TSuper> = TSub extends TSuper ? true : never;
export type JsonLdDocumentContractIsOntologyCompatible = Extends<JsonLdDocumentDto, JsonLdDocument>;
export type ContextTermValueContractIsOntologyCompatible = Extends<
  z.infer<typeof ContextTermValueSchema>,
  ContextTermValue
>;

/** A document whose validity is unknown until the ontology parser has run. */
export const UntrustedJsonLdSchema = z.record(z.string(), z.unknown());
export type UntrustedJsonLd = z.infer<typeof UntrustedJsonLdSchema>;
